import type { SubmitEvent } from 'react'
import { VpDrawer, VpDrawerBody, VpDrawerCloseButton, VpDrawerDialog, VpDrawerHeader } from '@vtmn-play/react'
import { useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { DEFAULT_FOLDER_EMOJI_UNIFIED } from '@/components/FavoriteViews/FavoriteViewEmoji'
import { FavoriteViewFolderFields } from '@/components/FavoriteViews/FavoriteViewFolderFields'
import { FavoriteViewFormFooter } from '@/components/FavoriteViews/FavoriteViewFormFooter'
import './favorite-view-panel.scss'

interface FavoriteViewFolderDrawerProps {
  onClose: () => void
  onSave: (input: { name: string, emoji: string }) => Promise<void>
  isEditing?: boolean
  initialName?: string
  initialEmoji?: string | null
}

export function FavoriteViewFolderDrawer({
  onClose,
  onSave,
  isEditing = false,
  initialName = '',
  initialEmoji,
}: Readonly<FavoriteViewFolderDrawerProps>) {
  const { t } = useTranslation()
  const title = t(isEditing ? 'favorite-views.management.edit-folder-title' : 'favorite-views.panel.add-folder')
  const [name, setName] = useState(initialName)
  const [emoji, setEmoji] = useState(initialEmoji ?? DEFAULT_FOLDER_EMOJI_UNIFIED)
  const [isSaving, setIsSaving] = useState(false)
  const handleSubmit = async (event: SubmitEvent<HTMLFormElement>) => {
    event.preventDefault()
    const trimmedName = name.trim()
    if (!trimmedName || isSaving)
      return

    setIsSaving(true)
    try {
      await onSave({ name: trimmedName, emoji })
    }
    finally {
      setIsSaving(false)
    }
  }

  return createPortal(
    <div className="favorite-view-panel__layer" data-testid="favorite-view-folder-layer">
      <div className="favorite-view-panel__backdrop" aria-hidden="true" />
      <VpDrawer closeOnClickOutside open onOpen={() => {}} onClose={onClose}>
        <VpDrawerDialog
          className="favorite-view-panel__dialog"
          aria-label={title}
          data-testid="favorite-view-folder-drawer"
        >
          <VpDrawerHeader>
            {title}
            <VpDrawerCloseButton aria-label={t('close')} />
          </VpDrawerHeader>
          <form className="favorite-view-panel__form" onSubmit={handleSubmit}>
            <VpDrawerBody className="favorite-view-panel__body">
              <FavoriteViewFolderFields
                name={name}
                selectedEmoji={emoji}
                onNameChange={setName}
                onEmojiSelect={setEmoji}
              />
            </VpDrawerBody>
            <FavoriteViewFormFooter
              onCancel={onClose}
              isSaveDisabled={!name.trim() || isSaving}
              saveLabel={isSaving ? t('favorite-views.panel.saving') : t('save')}
              saveTestId="favorite-view-folder-save"
            />
          </form>
        </VpDrawerDialog>
      </VpDrawer>
    </div>,
    document.body,
  )
}
